import React from 'react';

interface NumericKeyboardProps {
  onKeyPress: (key: string) => void;
  onClear: () => void;
  onDelete: () => void;
  onSubmit?: () => void;
  showSubmitButton?: boolean;
}

const KEY_BASE = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '14px',
  fontWeight: '700',
  fontSize: '1.5rem',
  height: '72px',
  cursor: 'pointer',
  userSelect: 'none' as const,
  transition: 'transform 0.1s, box-shadow 0.1s',
  WebkitTapHighlightColor: 'transparent',
};

const NumKey: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => {
  const [pressed, setPressed] = React.useState(false);
  return (
    <button
      type="button"
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => { setPressed(false); onClick(); }}
      onPointerLeave={() => setPressed(false)}
      style={{
        ...KEY_BASE,
        background: pressed ? '#1e3a8a' : 'linear-gradient(145deg, #1d4ed8, #1e40af)',
        color: '#ffffff',
        boxShadow: pressed
          ? '0 1px 2px rgba(0,0,0,0.3)'
          : '0 4px 8px rgba(29,78,216,0.35), inset 0 1px 0 rgba(255,255,255,0.12)',
        transform: pressed ? 'scale(0.94)' : 'scale(1)',
      }}
    >
      {label}
    </button>
  );
};

const ActionKey: React.FC<{ label: React.ReactNode; onClick: () => void; danger?: boolean }> = ({ label, onClick, danger }) => {
  const [pressed, setPressed] = React.useState(false);
  return (
    <button
      type="button"
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => { setPressed(false); onClick(); }}
      onPointerLeave={() => setPressed(false)}
      style={{
        ...KEY_BASE,
        fontSize: '1.25rem',
        background: pressed
          ? (danger ? '#b91c1c' : '#334155')
          : (danger ? 'linear-gradient(145deg, #ef4444, #dc2626)' : 'linear-gradient(145deg, #475569, #334155)'),
        color: '#ffffff',
        boxShadow: pressed
          ? '0 1px 2px rgba(0,0,0,0.3)'
          : (danger
              ? '0 4px 8px rgba(220,38,38,0.35), inset 0 1px 0 rgba(255,255,255,0.1)'
              : '0 4px 8px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.1)'),
        transform: pressed ? 'scale(0.94)' : 'scale(1)',
      }}
    >
      {label}
    </button>
  );
};

export const NumericKeyboard: React.FC<NumericKeyboardProps> = ({
  onKeyPress, onClear, onDelete, onSubmit, showSubmitButton = false,
}) => {
  const digits = ['1','2','3','4','5','6','7','8','9'];

  return (
    <div className="p-4 rounded-2xl mt-2" style={{ background: '#0f172a' }}>
      <div className="grid grid-cols-3 gap-3">
        {digits.map(k => <NumKey key={k} label={k} onClick={() => onKeyPress(k)} />)}

        <ActionKey
          danger
          label={<svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l6.414 6.414a2 2 0 001.414.586H19a2 2 0 002-2V7a2 2 0 00-2-2h-8.172a2 2 0 00-1.414.586L3 12z"/></svg>}
          onClick={onDelete}
        />

        <NumKey label="0" onClick={() => onKeyPress('0')} />

        <ActionKey
          label="C"
          onClick={onClear}
          danger
        />
      </div>

      {showSubmitButton && (
        <button
          type="button"
          onClick={onSubmit}
          className="w-full mt-3 py-5 rounded-xl text-white text-xl font-bold tracking-wide transition-all active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            boxShadow: '0 4px 16px rgba(37,99,235,0.45)',
          }}
        >
          ✓ Aceptar
        </button>
      )}
    </div>
  );
};
